import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Spinner SVG riutilizzabile
const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

// ItemCard estratto fuori da MenuSection per soddisfare ESLint prop-types
const ItemCard = ({ item, gridLayout, isItemAvailable, loadingItemId, onCustomize, onAddToCart }) => {
  const available = isItemAvailable(item.id);
  const loading = loadingItemId === item.id;

  return (
    <div
      className={`flex ${gridLayout ? 'flex-col items-center text-center' : 'flex-col sm:flex-row justify-between items-start sm:items-center'}
        p-4 border rounded-lg bg-gray-50 transition-colors duration-200
        ${available ? 'hover:bg-gray-100 border-gray-200' : 'opacity-50 cursor-not-allowed border-red-300'}`}
    >
      <div className={gridLayout ? 'mb-2' : ''}>
        <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
        {item.description ? (
          <p className="text-gray-600 text-sm mt-1">{item.description}</p>
        ) : null}
      </div>

      <div className={`flex ${gridLayout ? 'flex-col items-center' : 'flex-col sm:flex-row items-start sm:items-center'} space-y-2 sm:space-y-0 sm:space-x-2 mt-3 sm:mt-0`}>
        <span className="text-lg font-bold text-green-600 mr-2">&#8364;{item.price.toFixed(2)}</span>

        {available ? (
          <>
            {onCustomize && (
              <button
                onClick={() => onCustomize(item)}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md shadow-sm transition-colors duration-200 text-sm disabled:opacity-50"
              >
                Personalizza
              </button>
            )}
            <button
              onClick={() => onAddToCart(item)}
              disabled={loading}
              className={`flex items-center justify-center px-3 py-1 rounded-md shadow-sm transition-colors duration-200 text-sm
                ${loading ? 'bg-gray-300 text-gray-700 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
            >
              {loading ? <Spinner /> : 'Aggiungi'}
            </button>
          </>
        ) : (
          <span className="px-3 py-1 text-sm font-semibold text-red-700 bg-red-100 rounded-md">Non Disponibile</span>
        )}
      </div>
    </div>
  );
};

ItemCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.number.isRequired,
  }).isRequired,
  gridLayout: PropTypes.bool,
  isItemAvailable: PropTypes.func.isRequired,
  loadingItemId: PropTypes.string,
  onCustomize: PropTypes.func,
  onAddToCart: PropTypes.func.isRequired,
};

ItemCard.defaultProps = {
  gridLayout: false,
  loadingItemId: null,
  onCustomize: null,
};

/**
 * MenuSection - lista generica di prodotti con ricerca opzionale.
 */
const MenuSection = ({
  title,
  items,
  isItemAvailable,
  loadingItemId,
  onCustomize,
  onAddToCart,
  showSearch,
  gridLayout,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <section className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
      {title ? <h2 className="text-2xl font-bold mb-4 text-red-700">{title}</h2> : null}

      {showSearch && items.length > 6 && (
        <div className="mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca per nome o ingrediente..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
          />
          {searchTerm && (
            <p className="text-xs text-gray-500 mt-1">
              {filtered.length} risultat{filtered.length === 1 ? 'o' : 'i'} su {items.length}
            </p>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-gray-500 italic text-sm">
          Nessun prodotto trovato per &quot;{searchTerm}&quot;.
        </p>
      ) : gridLayout ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              gridLayout={gridLayout}
              isItemAvailable={isItemAvailable}
              loadingItemId={loadingItemId}
              onCustomize={onCustomize}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              gridLayout={gridLayout}
              isItemAvailable={isItemAvailable}
              loadingItemId={loadingItemId}
              onCustomize={onCustomize}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>
      )}
    </section>
  );
};

MenuSection.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array.isRequired,
  isItemAvailable: PropTypes.func.isRequired,
  loadingItemId: PropTypes.string,
  onCustomize: PropTypes.func,
  onAddToCart: PropTypes.func.isRequired,
  showSearch: PropTypes.bool,
  gridLayout: PropTypes.bool,
};

MenuSection.defaultProps = {
  title: '',
  loadingItemId: null,
  onCustomize: null,
  showSearch: true,
  gridLayout: false,
};

export default MenuSection;
